import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaChartPie, FaMoneyBillWave, FaFileInvoiceDollar, 
         FaArrowUp, FaArrowDown, FaCalendarAlt, FaWallet, FaChartLine, FaTimes, 
         FaFilter, FaDownload, FaSort, FaSearch, FaSortAmountDown, FaLayerGroup } from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../security/security/AuthContext';

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const [approvedWithdrawals, setApprovedWithdrawals] = useState(0);
  const [activeView, setActiveView] = useState('overview');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: ''
  });

  const expenseCategories = [
    'Development',
    'Maintenance', 
    'Marketing (Ad )',
    'Shares',
    'Infrastructure',
    'Office Expenses',
    'Customer Support',
    'Training',
    'Software Licenses',
    'Miscellaneous',
  ];

  const categoryIcons = {
    'Development': '💻',
    'Maintenance': '🔧',
    'Marketing (Ad )': '📣',
    'Shares': '📊',
    'Infrastructure': '🏢',
    'Office Expenses': '📌',
    'Customer Support': '🙋',
    'Training': '📚',
    'Software Licenses': '🔑',
    'Miscellaneous': '📦',
  };

  useEffect(() => {
    fetchExpenses();
    fetchFinancialSummary();
    fetchApprovedWithdrawals();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.globallequityfinance.com/api/admin/expenses', {
        headers: { Authorization: token }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedWithdrawals = async () => {
    try {
      const response = await axios.get('https://api.globallequityfinance.com/api/admin/withdrawals?status=APPROVED', {
        headers: { Authorization: token }
      });
      const totalWithdrawals = response.data.reduce((sum, withdrawal) => sum + withdrawal.amountToReceive, 0);
      setApprovedWithdrawals(totalWithdrawals);
    } catch (error) {
      console.error('Error fetching approved withdrawals:', error);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await axios.get('https://api.globallequityfinance.com/api/admin/expenses/summary', {
        headers: { Authorization: token }
      });
      setFinancialSummary(response.data);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedExpense) {
        await axios.put(
          `https://api.globallequityfinance.com/api/admin/expenses/${selectedExpense.id}`,
          formData,
          { headers: { Authorization: token } }
        );
      } else {
        await axios.post(
          'https://api.globallequityfinance.com/api/admin/expenses',
          formData,
          { headers: { Authorization: token } }
        );
      }
      setIsModalOpen(false);
      setSelectedExpense(null);
      setFormData({ category: '', description: '', amount: '' });
      fetchExpenses();
      fetchFinancialSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      category: expense.category,
      description: expense.description,
      amount: expense.amount.toString()
    });
    setIsModalOpen(true);
  };

  // Calculate actual available balance by subtracting approved withdrawals
  const calculateAvailableBalance = () => {
    if (!financialSummary) return 0;
    return financialSummary.totalRevenue - financialSummary.totalExpenses - approvedWithdrawals;
  };

  // Filter expenses based on category and search
  const filteredExpenses = expenses.filter(expense => {
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;
    const matchesSearch = searchQuery === '' || 
      expense.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      expense.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get percentage change for financial metrics
  const getPercentChange = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Calculate top expense categories
  const getTopCategories = () => {
    if (!financialSummary?.expensesByCategory) return [];
    return [...financialSummary.expensesByCategory]
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 3);
  };

  return (
    <div className="forex-pesa-expense-manager">
      {/* Tab Navigation */}
      <div className="forex-pesa-expense-tabs">
        <button 
          className={`forex-pesa-expense-tab ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          <FaChartPie /> Financial Overview
        </button>
        <button 
          className={`forex-pesa-expense-tab ${activeView === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveView('expenses')}
        >
          <FaFileInvoiceDollar /> Expense Management
        </button>
        <button 
          className={`forex-pesa-expense-tab ${activeView === 'analysis' ? 'active' : ''}`}
          onClick={() => setActiveView('analysis')}
        >
          <FaChartLine /> Expense Analysis
        </button>
      </div>

      {/* Overview Section */}
      {activeView === 'overview' && (
        <div className="forex-pesa-expense-overview">
          <div className="forex-pesa-expense-header">
            <h2>Financial Overview</h2>
            <div className="forex-pesa-expense-period">
              <FaCalendarAlt /> Current Period
            </div>
          </div>

          <div className="forex-pesa-expense-summary-cards">
            <div className="forex-pesa-expense-card forex-pesa-expense-revenue">
              <div className="forex-pesa-expense-card-icon">
                <FaMoneyBillWave />
              </div>
              <div className="forex-pesa-expense-card-content">
                <div className="forex-pesa-expense-card-title">Total Revenue</div>
                <div className="forex-pesa-expense-card-value">
                  KSH {financialSummary?.totalRevenue?.toLocaleString() || '0'}
                </div>
                <div className="forex-pesa-expense-card-trend positive">
                  <FaArrowUp /> 12.4% from last month
                </div>
              </div>
            </div>

            <div className="forex-pesa-expense-card forex-pesa-expense-expenses">
              <div className="forex-pesa-expense-card-icon">
                <FaFileInvoiceDollar />
              </div>
              <div className="forex-pesa-expense-card-content">
                <div className="forex-pesa-expense-card-title">Total Expenses</div>
                <div className="forex-pesa-expense-card-value">
                  KSH {financialSummary?.totalExpenses?.toLocaleString() || '0'}
                </div>
                <div className="forex-pesa-expense-card-trend negative">
                  <FaArrowUp /> 8.2% from last month
                </div>
              </div>
            </div>

            <div className="forex-pesa-expense-card forex-pesa-expense-withdrawals">
              <div className="forex-pesa-expense-card-icon">
                <FaWallet />
              </div>
              <div className="forex-pesa-expense-card-content">
                <div className="forex-pesa-expense-card-title">Total Withdrawals</div>
                <div className="forex-pesa-expense-card-value">
                  KSH {approvedWithdrawals.toLocaleString()}
                </div>
                <div className="forex-pesa-expense-card-trend negative">
                  <FaArrowUp /> 5.7% from last month
                </div>
              </div>
            </div>

            <div className="forex-pesa-expense-card forex-pesa-expense-balance">
              <div className="forex-pesa-expense-card-icon">
                <FaChartLine />
              </div>
              <div className="forex-pesa-expense-card-content">
                <div className="forex-pesa-expense-card-title">Available Balance</div>
                <div className="forex-pesa-expense-card-value">
                  KSH {calculateAvailableBalance().toLocaleString()}
                </div>
                <div className="forex-pesa-expense-card-trend positive">
                  <FaArrowUp /> 3.1% from last month
                </div>
              </div>
            </div>
          </div>

          <div className="forex-pesa-expense-grid">
            <div className="forex-pesa-expense-insights-card">
              <div className="forex-pesa-expense-insights-header">
                <h3>Top Expense Categories</h3>
              </div>
              <div className="forex-pesa-expense-top-categories">
                {getTopCategories().map((category, index) => (
                  <div className="forex-pesa-expense-top-category" key={category.category}>
                    <div className="forex-pesa-expense-category-icon">
                      {categoryIcons[category.category] || '📋'}
                    </div>
                    <div className="forex-pesa-expense-category-details">
                      <div className="forex-pesa-expense-category-name">
                        {category.category}
                      </div>
                      <div className="forex-pesa-expense-category-amount">
                        KSH {category.totalAmount.toLocaleString()}
                      </div>
                      <div className="forex-pesa-expense-category-bar">
                        <div 
                          className="forex-pesa-expense-category-progress"
                          style={{ width: `${category.percentageOfExpenses}%` }}
                        ></div>
                      </div>
                      <div className="forex-pesa-expense-category-percent">
                        {category.percentageOfExpenses.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="forex-pesa-expense-insights-card">
              <div className="forex-pesa-expense-insights-header">
                <h3>Recent Expenses</h3>
                <button className="forex-pesa-expense-view-all">
                  View All
                </button>
              </div>
              <div className="forex-pesa-expense-recent-list">
                {expenses.slice(0, 5).map(expense => (
                  <div className="forex-pesa-expense-recent-item" key={expense.id}>
                    <div className="forex-pesa-expense-recent-icon">
                      {categoryIcons[expense.category] || '📋'}
                    </div>
                    <div className="forex-pesa-expense-recent-details">
                      <div className="forex-pesa-expense-recent-title">
                        {expense.description}
                      </div>
                      <div className="forex-pesa-expense-recent-category">
                        {expense.category}
                      </div>
                    </div>
                    <div className="forex-pesa-expense-recent-right">
                      <div className="forex-pesa-expense-recent-amount">
                        KSH {expense.amount.toLocaleString()}
                      </div>
                      <div className="forex-pesa-expense-recent-date">
                        {format(new Date(expense.createdAt), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expenses Management Section */}
      {activeView === 'expenses' && (
        <div className="forex-pesa-expense-management">
          <div className="forex-pesa-expense-header">
            <h2>Expense Management</h2>
            <button
              className="forex-pesa-expense-add-button"
              onClick={() => setIsModalOpen(true)}
            >
              <FaPlus /> Add Expense
            </button>
          </div>

          <div className="forex-pesa-expense-filters">
            <div className="forex-pesa-expense-search">
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FaSearch className="forex-pesa-expense-search-icon" />
            </div>
            
            <div className="forex-pesa-expense-filter-wrapper">
              <div className="forex-pesa-expense-filter">
                <FaFilter className="forex-pesa-expense-filter-icon" />
                <select 
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {expenseCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              
              <button className="forex-pesa-expense-export-button">
                <FaDownload /> Export
              </button>
            </div>
          </div>

          <div className="forex-pesa-expense-table-container">
            {loading ? (
              <div className="forex-pesa-expense-loading">
                <div className="forex-pesa-expense-spinner"></div>
                <p>Loading expenses...</p>
              </div>
            ) : filteredExpenses.length === 0 ? (
              <div className="forex-pesa-expense-empty">
                <div className="forex-pesa-expense-empty-icon">
                  <FaFileInvoiceDollar />
                </div>
                <h3>No Expenses Found</h3>
                <p>Add your first expense or try a different search</p>
                <button
                  className="forex-pesa-expense-add-button"
                  onClick={() => setIsModalOpen(true)}
                >
                  <FaPlus /> Add Expense
                </button>
              </div>
            ) : (
              <table className="forex-pesa-expense-table">
                <thead>
                  <tr>
                    <th>
                      <div className="forex-pesa-expense-th-content">
                        Category <FaSort className="forex-pesa-expense-sort-icon" />
                      </div>
                    </th>
                    <th>
                      <div className="forex-pesa-expense-th-content">
                        Description <FaSort className="forex-pesa-expense-sort-icon" />
                      </div>
                    </th>
                    <th>
                      <div className="forex-pesa-expense-th-content">
                        Amount <FaSort className="forex-pesa-expense-sort-icon" />
                      </div>
                    </th>
                    <th>
                      <div className="forex-pesa-expense-th-content">
                        Date <FaSort className="forex-pesa-expense-sort-icon" />
                      </div>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="forex-pesa-expense-table-row">
                      <td>
                        <div className="forex-pesa-expense-category">
                          <span className="forex-pesa-expense-category-icon">
                            {categoryIcons[expense.category] || '📋'}
                          </span>
                          {expense.category}
                        </div>
                      </td>
                      <td>
                        <div className="forex-pesa-expense-description">
                          {expense.description}
                        </div>
                      </td>
                      <td>
                        <div className="forex-pesa-expense-amount">
                          KSH {expense.amount.toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <div className="forex-pesa-expense-date">
                          {format(new Date(expense.createdAt), 'MMM d, yyyy')}
                          <div className="forex-pesa-expense-time">
                            {format(new Date(expense.createdAt), 'h:mm a')}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="forex-pesa-expense-actions">
                          <button
                            className="forex-pesa-expense-action-button forex-pesa-expense-delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Analysis Section */}
      {activeView === 'analysis' && (
        <div className="forex-pesa-expense-analysis">
          <div className="forex-pesa-expense-header">
            <h2>Expense Analysis</h2>
            <div className="forex-pesa-expense-period-selector">
              <button className="forex-pesa-expense-period-button active">Monthly</button>
              <button className="forex-pesa-expense-period-button">Quarterly</button>
              <button className="forex-pesa-expense-period-button">Yearly</button>
            </div>
          </div>

          <div className="forex-pesa-expense-analysis-grid">
            <div className="forex-pesa-expense-breakdown-card">
              <div className="forex-pesa-expense-card-header">
                <h3>Expense Breakdown by Category</h3>
              </div>
              <div className="forex-pesa-expense-category-breakdown">
                {financialSummary?.expensesByCategory?.map((category) => (
                  <div className="forex-pesa-expense-breakdown-item" key={category.category}>
                    <div className="forex-pesa-expense-breakdown-header">
                      <div className="forex-pesa-expense-breakdown-icon">
                        {categoryIcons[category.category] || '📋'}
                      </div>
                      <div className="forex-pesa-expense-breakdown-name">
                        {category.category}
                      </div>
                      <div className="forex-pesa-expense-breakdown-percent">
                        {category.percentageOfExpenses.toFixed(1)}%
                      </div>
                    </div>
                    <div className="forex-pesa-expense-breakdown-bar">
                      <div 
                        className="forex-pesa-expense-breakdown-progress"
                        style={{ 
                          width: `${category.percentageOfExpenses}%`,
                          backgroundColor: getColorForCategory(category.category)
                        }}
                      ></div>
                    </div>
                    <div className="forex-pesa-expense-breakdown-amount">
                      KSH {category.totalAmount.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="forex-pesa-expense-monthly-card">
              <div className="forex-pesa-expense-card-header">
                <h3>Monthly Expense Trends</h3>
              </div>
              <div className="forex-pesa-expense-monthly-placeholder">
                <div className="forex-pesa-expense-chart-placeholder">
                  <div className="forex-pesa-expense-chart-bar" style={{ height: '65%' }}></div>
                  <div className="forex-pesa-expense-chart-bar" style={{ height: '40%' }}></div>
                  <div className="forex-pesa-expense-chart-bar" style={{ height: '75%' }}></div>
                  <div className="forex-pesa-expense-chart-bar" style={{ height: '55%' }}></div>
                  <div className="forex-pesa-expense-chart-bar" style={{ height: '90%' }}></div>
                  <div className="forex-pesa-expense-chart-bar" style={{ height: '60%' }}></div>
                </div>
                <div className="forex-pesa-expense-chart-legend">
                  <div className="forex-pesa-expense-legend-item">
                    <div className="forex-pesa-expense-legend-color primary"></div>
                    <div>Expenses</div>
                  </div>
                  <div className="forex-pesa-expense-legend-item">
                    <div className="forex-pesa-expense-legend-color secondary"></div>
                    <div>Revenue</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="forex-pesa-expense-summary-table">
            <div className="forex-pesa-expense-card-header">
              <h3>Financial Metrics</h3>
            </div>
            <table className="forex-pesa-expense-metrics-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Current Period</th>
                  <th>Previous Period</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Revenue</td>
                  <td>KSH {financialSummary?.totalRevenue?.toLocaleString() || '0'}</td>
                  <td>KSH {(financialSummary?.totalRevenue * 0.9).toFixed(0).toLocaleString() || '0'}</td>
                  <td className="forex-pesa-expense-positive">+10.0%</td>
                </tr>
                <tr>
                  <td>Total Expenses</td>
                  <td>KSH {financialSummary?.totalExpenses?.toLocaleString() || '0'}</td>
                  <td>KSH {(financialSummary?.totalExpenses * 0.92).toFixed(0).toLocaleString() || '0'}</td>
                  <td className="forex-pesa-expense-negative">+8.2%</td>
                </tr>
                <tr>
                  <td>Profit Margin</td>
                  <td>
                    {financialSummary ? ((financialSummary.totalRevenue - financialSummary.totalExpenses) / 
                      financialSummary.totalRevenue * 100).toFixed(1) : '0'}%
                  </td>
                  <td>
                    {financialSummary ? (((financialSummary.totalRevenue * 0.9) - (financialSummary.totalExpenses * 0.92)) / 
                      (financialSummary.totalRevenue * 0.9) * 100).toFixed(1) : '0'}%
                  </td>
                  <td className="forex-pesa-expense-positive">+1.8%</td>
                </tr>
                <tr>
                  <td>Expense Ratio</td>
                  <td>
                    {financialSummary ? (financialSummary.totalExpenses / 
                      financialSummary.totalRevenue * 100).toFixed(1) : '0'}%
                  </td>
                  <td>
                    {financialSummary ? ((financialSummary.totalExpenses * 0.92) / 
                      (financialSummary.totalRevenue * 0.9) * 100).toFixed(1) : '0'}%
                  </td>
                  <td className="forex-pesa-expense-negative">+1.2%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Expense Modal */}
      {isModalOpen && (
        <div className="forex-pesa-expense-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="forex-pesa-expense-modal" onClick={(e) => e.stopPropagation()}>
            <div className="forex-pesa-expense-modal-header">
              <h3>{selectedExpense ? 'Edit Expense' : 'Add New Expense'}</h3>
              <button
                className="forex-pesa-expense-modal-close"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedExpense(null);
                  setFormData({ category: '', description: '', amount: '' });
                }}
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="forex-pesa-expense-modal-form">
              <div className="forex-pesa-expense-form-group">
                <label>Category</label>
                <div className="forex-pesa-expense-select-wrapper">
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="forex-pesa-expense-select"
                  >
                    <option value="">Select Category</option>
                    {expenseCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <FaLayerGroup className="forex-pesa-expense-select-icon" />
                </div>
              </div>
              
              <div className="forex-pesa-expense-form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter expense description"
                  required
                  className="forex-pesa-expense-input"
                />
              </div>
              
              <div className="forex-pesa-expense-form-group">
                <label>Amount (KSH)</label>
                <div className="forex-pesa-expense-amount-wrapper">
                  <span className="forex-pesa-expense-currency">KSH</span>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount"
                    min="0"
                    step="0.01"
                    required
                    className="forex-pesa-expense-input"
                  />
                </div>
              </div>
              
              <div className="forex-pesa-expense-modal-footer">
                <button
                  type="button"
                  className="forex-pesa-expense-button-secondary"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedExpense(null);
                    setFormData({ category: '', description: '', amount: '' });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="forex-pesa-expense-button-primary">
                  {selectedExpense ? 'Update Expense' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

        <style jsx>{`

        /* Expense Management Component */
.forex-pesa-expense-manager {
  background-color: #f7fafc;
  border-radius: 16px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}

/* Tab Navigation */
.forex-pesa-expense-tabs {
  display: flex;
  background-color: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 20px;
}

.forex-pesa-expense-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.forex-pesa-expense-tab:hover {
  color: #3182ce;
}

.forex-pesa-expense-tab.active {
  color: #3182ce;
  font-weight: 600;
}

.forex-pesa-expense-tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #3182ce, #63b3ed);
  border-radius: 3px 3px 0 0;
}

/* Common Header Styles */
.forex-pesa-expense-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 30px;
  background-color: white;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.forex-pesa-expense-period {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4a5568;
  background-color: #edf2f7;
  padding: 6px 12px;
  border-radius: 8px;
}

.forex-pesa-expense-add-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #3182ce, #63b3ed);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-expense-add-button:hover {
  background: linear-gradient(135deg, #2c5282, #4299e1);
  box-shadow: 0 4px 10px rgba(49, 130, 206, 0.2);
  transform: translateY(-1px);
}

/* Overview Section */
.forex-pesa-expense-overview {
  background-color: #f7fafc;
  padding-bottom: 30px;
}

.forex-pesa-expense-summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 20px 30px;
}

.forex-pesa-expense-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  transition: all 0.3s ease;
}

.forex-pesa-expense-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.forex-pesa-expense-card-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border-radius: 10px;
  flex-shrink: 0;
}

.forex-pesa-expense-revenue .forex-pesa-expense-card-icon {
  background-color: #ebf8ff;
  color: #3182ce;
}

.forex-pesa-expense-expenses .forex-pesa-expense-card-icon {
  background-color: #feebc8;
  color: #dd6b20;
}

.forex-pesa-expense-withdrawals .forex-pesa-expense-card-icon {
  background-color: #e9d8fd;
  color: #805ad5;
}

.forex-pesa-expense-balance .forex-pesa-expense-card-icon {
  background-color: #c6f6d5;
  color: #38a169;
}

.forex-pesa-expense-card-content {
  flex: 1;
}

.forex-pesa-expense-card-title {
  font-size: 14px;
  color: #718096;
  margin-bottom: 6px;
}

.forex-pesa-expense-card-value {
  font-size: 22px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
}

.forex-pesa-expense-card-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
}

.forex-pesa-expense-card-trend.positive {
  color: #38a169;
}

.forex-pesa-expense-card-trend.negative {
  color: #e53e3e;
}

.forex-pesa-expense-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  padding: 0 30px;
}

.forex-pesa-expense-insights-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.forex-pesa-expense-insights-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.forex-pesa-expense-insights-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.forex-pesa-expense-view-all {
  font-size: 12px;
  color: #3182ce;
  background: none;
  border: none;
  cursor: pointer;
}

.forex-pesa-expense-top-categories {
  padding: 20px;
}

.forex-pesa-expense-top-category {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.forex-pesa-expense-top-category:last-child {
  margin-bottom: 0;
}

.forex-pesa-expense-category-icon {
  width: 40px;
  height: 40px;
  background-color: #f7fafc;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.forex-pesa-expense-category-details {
  flex: 1;
}

.forex-pesa-expense-category-name {
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 4px;
}

.forex-pesa-expense-category-amount {
  font-size: 14px;
  color: #718096;
  margin-bottom: 8px;
}

.forex-pesa-expense-category-bar {
  height: 6px;
  background-color: #edf2f7;
  border-radius: 3px;
  margin-bottom: 4px;
}

.forex-pesa-expense-category-progress {
  height: 100%;
  background: linear-gradient(90deg, #3182ce, #63b3ed);
  border-radius: 3px;
}

.forex-pesa-expense-category-percent {
  font-size: 12px;
  color: #3182ce;
  text-align: right;
}

.forex-pesa-expense-recent-list {
  padding: 10px 20px;
}

.forex-pesa-expense-recent-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;
}

.forex-pesa-expense-recent-item:last-child {
  border-bottom: none;
}

.forex-pesa-expense-recent-icon {
  width: 36px;
  height: 36px;
  background-color: #f7fafc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.forex-pesa-expense-recent-details {
  flex: 1;
}

.forex-pesa-expense-recent-title {
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 2px;
  font-size: 14px;
}

.forex-pesa-expense-recent-category {
  font-size: 12px;
  color: #718096;
}

.forex-pesa-expense-recent-right {
  text-align: right;
}

.forex-pesa-expense-recent-amount {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 2px;
  font-size: 14px;
}

.forex-pesa-expense-recent-date {
  font-size: 12px;
  color: #718096;
}

/* Expense Management Section */
.forex-pesa-expense-management {
  background-color: #f7fafc;
  padding-bottom: 30px;
}

.forex-pesa-expense-filters {
  padding: 20px 30px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
}

.forex-pesa-expense-search {
  position: relative;
  width: 300px;
  max-width: 100%;
}

.forex-pesa-expense-search input {
  width: 100%;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 40px 0 15px;
  font-size: 14px;
  background-color: white;
  transition: all 0.2s ease;
}

.forex-pesa-expense-search input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.forex-pesa-expense-search-icon {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 14px;
}

.forex-pesa-expense-filter-wrapper {
  display: flex;
  gap: 15px;
}

.forex-pesa-expense-filter {
  position: relative;
  width: 200px;
}

.forex-pesa-expense-filter select {
  width: 100%;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 15px 0 40px;
  font-size: 14px;
  background-color: white;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-expense-filter select:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.forex-pesa-expense-filter-icon {
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 14px;
}

.forex-pesa-expense-export-button {
  height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 15px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-expense-export-button:hover {
  background-color: #edf2f7;
  color: #3182ce;
}

.forex-pesa-expense-table-container {
  padding: 0 30px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin: 0 30px;
  overflow: hidden;
}

.forex-pesa-expense-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #718096;
}

.forex-pesa-expense-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(49, 130, 206, 0.2);
  border-top-color: #3182ce;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.forex-pesa-expense-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  text-align: center;
}

.forex-pesa-expense-empty-icon {
  width: 70px;
  height: 70px;
  background-color: #edf2f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #3182ce;
  margin-bottom: 20px;
}

.forex-pesa-expense-empty h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  color: #2d3748;
}

.forex-pesa-expense-empty p {
  margin: 0 0 20px 0;
  color: #718096;
  font-size: 14px;
}

.forex-pesa-expense-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.forex-pesa-expense-th-content {
  display: flex;
  align-items: center;
  gap: 5px;
}

.forex-pesa-expense-sort-icon {
  font-size: 12px;
  color: #a0aec0;
}

.forex-pesa-expense-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-table td {
  padding: 14px 20px;
  vertical-align: middle;
  font-size: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-table-row:last-child td {
  border-bottom: none;
}

.forex-pesa-expense-table-row:hover {
  background-color: #f7fafc;
}

.forex-pesa-expense-category {
  display: flex;
  align-items: center;
  gap: 8px;
}

.forex-pesa-expense-category-icon {
  font-size: 16px;
}

.forex-pesa-expense-description {
  font-weight: 500;
  color: #2d3748;
}

.forex-pesa-expense-amount {
  font-weight: 600;
  color: #2d3748;
}

.forex-pesa-expense-date {
  color: #4a5568;
}

.forex-pesa-expense-time {
  font-size: 12px;
  color: #718096;
  margin-top: 2px;
}

.forex-pesa-expense-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.forex-pesa-expense-action-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.forex-pesa-expense-edit {
  background-color: #ebf8ff;
  color: #3182ce;
}

.forex-pesa-expense-edit:hover {
  background-color: #bee3f8;
}

.forex-pesa-expense-delete {
  background-color: #fff5f5;
  color: #e53e3e;
}

.forex-pesa-expense-delete:hover {
  background-color: #fed7d7;
}

/* Analysis Section */
.forex-pesa-expense-analysis {
  background-color: #f7fafc;
  padding-bottom: 30px;
}

.forex-pesa-expense-period-selector {
  display: flex;
  gap: 10px;
}

.forex-pesa-expense-period-button {
  background-color: #edf2f7;
  border: none;
  border-radius: 6px;
  padding: 8px 15px;
  font-size: 14px;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-expense-period-button.active {
  background-color: #3182ce;
  color: white;
}

.forex-pesa-expense-analysis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  padding: 20px 30px;
}

.forex-pesa-expense-breakdown-card,
.forex-pesa-expense-monthly-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.forex-pesa-expense-card-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.forex-pesa-expense-category-breakdown {
  padding: 20px;
}

.forex-pesa-expense-breakdown-item {
  margin-bottom: 20px;
}

.forex-pesa-expense-breakdown-item:last-child {
  margin-bottom: 0;
}

.forex-pesa-expense-breakdown-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.forex-pesa-expense-breakdown-icon {
  width: 30px;
  height: 30px;
  background-color: #f7fafc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-right: 10px;
}

.forex-pesa-expense-breakdown-name {
  flex: 1;
  font-weight: 500;
  color: #2d3748;
}

.forex-pesa-expense-breakdown-percent {
  font-size: 14px;
  font-weight: 600;
  color: #3182ce;
}

.forex-pesa-expense-breakdown-bar {
  height: 8px;
  background-color: #edf2f7;
  border-radius: 4px;
  margin-bottom: 5px;
}

.forex-pesa-expense-breakdown-progress {
  height: 100%;
  border-radius: 4px;
}

.forex-pesa-expense-breakdown-amount {
  font-size: 14px;
  color: #718096;
  text-align: right;
}

.forex-pesa-expense-monthly-placeholder {
  padding: 20px;
}

.forex-pesa-expense-chart-placeholder {
  display: flex;
  align-items: flex-end;
  height: 200px;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-chart-bar {
  flex: 1;
  background: linear-gradient(180deg, #3182ce, #63b3ed);
  border-radius: 8px 8px 0 0;
  position: relative;
}

.forex-pesa-expense-chart-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(180deg, rgba(66, 153, 225, 0.2), transparent);
  border-radius: 8px 8px 0 0;
}

.forex-pesa-expense-chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.forex-pesa-expense-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4a5568;
}

.forex-pesa-expense-legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.forex-pesa-expense-legend-color.primary {
  background-color: #3182ce;
}

.forex-pesa-expense-legend-color.secondary {
  background-color: #9f7aea;
}

.forex-pesa-expense-summary-table {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin: 0 30px;
}

.forex-pesa-expense-metrics-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.forex-pesa-expense-metrics-table th {
  padding: 14px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  background-color: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-metrics-table td {
  padding: 14px 20px;
  font-size: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.forex-pesa-expense-metrics-table tr:last-child td {
  border-bottom: none;
}

.forex-pesa-expense-positive {
  color: #38a169;
  font-weight: 500;
}

.forex-pesa-expense-negative {
  color: #e53e3e;
  font-weight: 500;
}

/* Modal */
.forex-pesa-expense-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.forex-pesa-expense-modal {
  background-color: white;
  border-radius: 12px;
  width: 95%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.forex-pesa-expense-modal-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.forex-pesa-expense-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.forex-pesa-expense-modal-close {
  background: none;
  border: none;
  color: #a0aec0;
  font-size: 18px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.forex-pesa-expense-modal-close:hover {
  background-color: #f7fafc;
  color: #e53e3e;
}

.forex-pesa-expense-modal-form {
  padding: 20px;
}

.forex-pesa-expense-form-group {
  margin-bottom: 20px;
}

.forex-pesa-expense-form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
}

.forex-pesa-expense-select-wrapper {
  position: relative;
}

.forex-pesa-expense-select {
  width: 90%;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 15px;
  font-size: 14px;
  appearance: none;
  background-color: white;
  transition: all 0.2s ease;
}

.forex-pesa-expense-select:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.forex-pesa-expense-select-icon {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.forex-pesa-expense-input {
  width: 90%;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 15px;
  font-size: 14px;
  background-color: white;
  transition: all 0.2s ease;
}

.forex-pesa-expense-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.forex-pesa-expense-amount-wrapper {
  position: relative;
}

.forex-pesa-expense-currency {
  position: absolute;
  top: 0;
  left: 0;
  height: 42px;
  display: flex;
  align-items: center;
  padding: 0 15px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-right: none;
  border-radius: 8px 0 0 8px;
  color: #4a5568;
  font-weight: 500;
}

.forex-pesa-expense-amount-wrapper .forex-pesa-expense-input {
  padding-left: 60px;
}

.forex-pesa-expense-modal-footer {
  padding: 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.forex-pesa-expense-button-secondary {
  height: 42px;
  padding: 0 20px;
  background-color: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-expense-button-secondary:hover {
  background-color: #f7fafc;
  color: #3182ce;
}

.forex-pesa-expense-button-primary {
  height: 42px;
  padding: 0 20px;
  background: linear-gradient(135deg, #3182ce, #63b3ed);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-expense-button-primary:hover {
  background: linear-gradient(135deg, #2c5282, #4299e1);
  box-shadow: 0 4px 10px rgba(49, 130, 206, 0.2);
  transform: translateY(-1px);
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .forex-pesa-expense-grid,
  .forex-pesa-expense-analysis-grid {
    grid-template-columns: 1fr;
  }
  
  .forex-pesa-expense-summary-cards {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    padding: 15px;
  }
  
  .forex-pesa-expense-card {
    padding: 15px;
  }
  
  .forex-pesa-expense-filters {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px;
  }
  
  .forex-pesa-expense-search {
    width: 100%;
  }
  
  .forex-pesa-expense-filter-wrapper {
    width: 100%;
    justify-content: space-between;
  }
  
  .forex-pesa-expense-filter {
    width: 100%;
    max-width: 200px;
  }
  
  .forex-pesa-expense-table-container,
  .forex-pesa-expense-summary-table {
    margin: 0 15px;
  }
}

@media (max-width: 768px) {
  .forex-pesa-expense-tabs {
    overflow-x: auto;
    padding: 0 15px;
  }
  
  .forex-pesa-expense-tab {
    padding: 12px 15px;
    white-space: nowrap;
  }
  
  .forex-pesa-expense-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    padding: 15px;
  }
  
  .forex-pesa-expense-card-value {
    font-size: 18px;
  }
  
  .forex-pesa-expense-recent-item {
    flex-wrap: wrap;
  }
  
  .forex-pesa-expense-recent-right {
    margin-top: 10px;
    width: 100%;
    text-align: left;
    padding-left: 51px; /* Align with content after icon */
  }
}

@media (max-width: 576px) {
  .forex-pesa-expense-filter-wrapper {
    flex-direction: column;
  }
  
  .forex-pesa-expense-filter {
    max-width: 100%;
  }
  
  .forex-pesa-expense-period-selector {
    width: 100%;
    justify-content: space-between;
  }
  
  .forex-pesa-expense-period-button {
    flex: 1;
    text-align: center;
    padding: 8px 10px;
    font-size: 12px;
  }
  
  .forex-pesa-expense-table {
    min-width: 650px; /* Force horizontal scroll on small screens */
  }
}

          `}</style>


    </div>
  );
};

// Helper function to get color for category
function getColorForCategory(category) {
  const colors = {
    'Development': '#4C51BF',
    'Maintenance': '#38B2AC', 
    'Marketing (Ad )': '#ED8936',
    'Shares': '#667EEA',
    'Infrastructure': '#9F7AEA',
    'Office Expenses': '#48BB78',
    'Customer Support': '#4299E1',
    'Training': '#F687B3',
    'Software Licenses': '#ECC94B',
    'Miscellaneous': '#A0AEC0',
  };
  
  return colors[category] || '#CBD5E0';
}

export default ExpenseManagement;
